import { useContext, useEffect, useMemo, useState } from "react"
import { JSONSchema7 } from "json-schema"
import { debounce } from "lodash"
import { getAnnotationFieldSchema, getPropertySchema } from "resha"
import { Loader } from "dash-styled"
import {
    AnnotationFieldContent,
    AnnotationFieldKeyValue,
    AnnotationFields
} from "universal-schema/lib/1.0.0/annotation"

import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { DataFieldType } from "../../../../../../../Models/DataProgram/DocumentExtraction"
import { DocumentsExtractionContext } from "../../../DocumentsExtractionContext"
import { FieldModalChildProps } from "../../../Common/Modals/FieldModal"
import { AnnotationFieldsStateContext } from "../../AnnotationFieldsStateContext"
import { BoundingBoxStatus } from "../../constants"
import { PrimitiveInstructions } from "./Primitive"
// import { TableInstructions } from "./Table"

// enum Primitives {
//     String = "String",
//     Integer = "Integer",
//     Float = "Float",
//     Boolean = "Boolean",
//     Date = "Date",
//     SingleChoice = "SingleChoice",
//     MultipleChoice = "MultipleChoice"
// }

export const BoundingBoxInstructions = ({
    isValid: isFormValid,
    formData,
    onSubmit,
    setValid,
    setValidation
}: FieldModalChildProps & {
    onSubmit: (annotation?: AnnotationFields[""] | undefined) => void
}) => {
    const requireAnnotations =
        formData?.options?.data?.requireAnnotations?.selection

    const {
        status,
        setChanged,
        dismissBoundingBox,
        makeCurrentBoundingBoxesActive
    } = useContext(AnnotationFieldsStateContext)

    const { appSchema, getAppSchema, findOutputId } = useContext(
        DocumentsExtractionContext
    )

    const outputSchema = hasValue(appSchema)
        ? getValue(appSchema)?.outputSchema
        : undefined

    const [prevFormData, setPrevFormData] = useState<Partial<DataFieldType>>()

    const getAppSchemaDebounced = useMemo(
        () =>
            debounce(field => {
                getAppSchema([field])
            }, 400),
        [getAppSchema]
    )

    const isApproved = status === BoundingBoxStatus.TRUSTED

    useEffect(() => {
        if (
            isApproved &&
            prevFormData !== undefined &&
            prevFormData !== formData
        ) {
            makeCurrentBoundingBoxesActive()
            setChanged(true)
        }
        setPrevFormData(formData)
    }, [
        isApproved,
        prevFormData,
        formData,
        appSchema,
        setChanged,
        makeCurrentBoundingBoxesActive
    ])

    useEffect(() => {
        if (requireAnnotations) {
            if (!outputSchema) {
                setValidation(() => () => {})
                setValid(false)
            }
        } else {
            setValidation(undefined)
        }
    }, [requireAnnotations, outputSchema, setValid, setValidation])

    useEffect(() => {
        if (requireAnnotations && isFormValid) {
            getAppSchemaDebounced(formData)
        } else {
            getAppSchemaDebounced.cancel()
        }
    }, [formData, isFormValid, requireAnnotations, getAppSchemaDebounced])

    useEffect(() => {
        if (!requireAnnotations) {
            dismissBoundingBox()
        }
    }, [requireAnnotations, dismissBoundingBox])

    const fieldSchema = useMemo(() => {
        const fieldId = formData?.fieldId?.data
        if (fieldId) {
            const annotationsOutputId = findOutputId(fieldId)
            return outputSchema && annotationsOutputId
                ? getAnnotationFieldSchema(
                      outputSchema as JSONSchema7,
                      annotationsOutputId
                  )
                : undefined
        }
    }, [outputSchema, formData?.fieldId?.data, findOutputId])

    const contentSchema = useMemo(
        () =>
            fieldSchema !== undefined
                ? getPropertySchema(fieldSchema, "content")
                : undefined,
        [fieldSchema]
    )

    // useEffect(() => {
    //     if (
    //         currentAnnotations &&
    //         status &&
    //         (status === BoundingBoxStatus.NON_TRUSTED ||
    //             status === BoundingBoxStatus.TRUSTED)
    //     ) {
    //         if (isKeyValueAnnotation(currentAnnotations[0])) {
    //             // TODO: change formData key,value parameter
    //         } else if (isTableAnnotation(currentAnnotations[0])) {
    //             // TODO: Table type implementation, apply table type if type === undefined
    //             // onChange({
    //             //     ...formData,
    //             //     options: {
    //             //         data: {
    //             //             type: "Table",
    //             //             contentType: "Table"
    //             //         }
    //             //     }
    //             // })
    //         } else if (type === undefined) {
    //             // set string type by default for primitive annotations
    //             updateFormData({
    //                 ...formData,
    //                 options: {
    //                     data: {
    //                         type: Primitives.String,
    //                         contentType: Primitives.String
    //                     }
    //                 }
    //             })
    //         }
    //     }
    // }, [])

    // const isPrimitive = true
    // Object.values(Primitives).some( // TODO: Table type implementation
    //     primitive => primitive === formData.options?.data?.type
    // )

    // const isTable = formData.options?.data?.type === "Table" // TODO: Table type implementation

    const handleSubmit = (data: unknown) => {
        const annotations = data as (
            | AnnotationFieldContent
            | AnnotationFieldKeyValue
        )[]

        onSubmit(annotations)
    }

    if (!requireAnnotations) {
        return null
    }

    if (appSchema === "loading") {
        return (
            <div className="h-48 mt-7 mb-2.5 flex justify-center items-center">
                <Loader />
            </div>
        )
    }

    if (
        !isFormValid ||
        !status ||
        !outputSchema ||
        !contentSchema // TODO: add key,value parameter to condition
    ) {
        return null
    }

    return (
        <PrimitiveInstructions
            rootSchema={outputSchema as JSONSchema7}
            contentSchema={contentSchema}
            setValidation={setValidation}
            setValid={setValid}
            onSubmit={handleSubmit}
        />
    )
}
